/**
 * @fileoverview Connect recorded email/deadline processing to operational inspection and explicit proposal review. Backend pipeline records own status and allowed transitions; the workspace owns filters and focused edit/approve/reject intent. `kc-internal-backend/functions/ai_calendar/__init__.py` maps extraction ownership. Reading processing history does not extract another document or publish a proposed event.
 */

import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { AppDataTable, AppColumn } from '../shared/AppDataTable';
import { AppButton, AppTag, AppModal, AppDropdown, AppTextInput, AppTextarea, AppSpinner, AppTooltip } from '../shared';
import api from '../../api';
import ToastContext from '../../ToastContext';

const EMAIL_STATUS_OPTIONS = [
    { label: 'All', value: 'all' },
    { label: 'Received', value: 'RECEIVED' },
    { label: 'Parsed', value: 'PARSED' },
    { label: 'Extracted', value: 'EXTRACTED' },
    { label: 'Processed', value: 'PROCESSED' },
    { label: 'Error', value: 'ERROR' }
];

const DEADLINE_STATUS_OPTIONS = [
    { label: 'All', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Modified', value: 'modified' },
    { label: 'Rejected', value: 'rejected' }
];



function AICalendarPipeline() {
    const [stats, setStats] = useState(null);
    const [emails, setEmails] = useState([]);
    const [deadlines, setDeadlines] = useState([]);
    const [loadingStats, setLoadingStats] = useState(false);
    const [loadingEmails, setLoadingEmails] = useState(false);
    const [loadingDeadlines, setLoadingDeadlines] = useState(false);
    const [emailStatusFilter, setEmailStatusFilter] = useState('all');
    const [deadlineStatusFilter, setDeadlineStatusFilter] = useState('all');
    const [matterFilter, setMatterFilter] = useState('');
    const [detailDialogVisible, setDetailDialogVisible] = useState(false);
    const [selectedEmailDetail, setSelectedEmailDetail] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [modifyDialogVisible, setModifyDialogVisible] = useState(false);
    const [modifyingDeadline, setModifyingDeadline] = useState(null);
    const [modifyDate, setModifyDate] = useState('');
    const [modifyReason, setModifyReason] = useState('');
    const [modifySubmitting, setModifySubmitting] = useState(false);
    const showToast = useContext(ToastContext);

    const emailStatusOptions = EMAIL_STATUS_OPTIONS;
    const deadlineStatusOptions = DEADLINE_STATUS_OPTIONS;

    const fetchStats = useCallback(

    async () => {
        setLoadingStats(true);
        try {
            const response = await api.get('/ai-calendar/stats');
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching AI Calendar stats:', error);
            showToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load AI Calendar stats',
                life: 3000
            });
        } finally {
            setLoadingStats(false);
        }
    }, [showToast]);

    const fetchEmails = useCallback(

    async () => {
        setLoadingEmails(true);
        try {
            const params = { limit: 50, offset: 0 };
            if (emailStatusFilter !== 'all') {
                params.status = emailStatusFilter;
            }
            const response = await api.get('/ai-calendar/emails', { params });
            setEmails(response.data.emails || []);
        } catch (error) {
            console.error('Error fetching AI Calendar emails:', error);
            showToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load AI Calendar emails',
                life: 3000
            });
        } finally {
            setLoadingEmails(false);
        }
    }, [emailStatusFilter, showToast]);

    const getBackendStatusesForFilter = useCallback(

    (filter) => {
        if (filter === 'pending') return ['EXTRACTED', 'PENDING_APPROVAL'];
        if (filter === 'approved') return ['APPROVED', 'SYNCED'];
        if (filter === 'modified') return ['MODIFIED'];
        if (filter === 'rejected') return ['REJECTED'];
        return null;
    }, []);

    const fetchDeadlines = useCallback(

    async () => {
        setLoadingDeadlines(true);
        try {
            const params = { limit: 100, offset: 0 };
            if (matterFilter) {
                params.matter_number = matterFilter;
            }
            const response = await api.get('/ai-calendar/deadlines', { params });
            let results = response.data.deadlines || [];
            const allowedStatuses = getBackendStatusesForFilter(deadlineStatusFilter);
            if (allowedStatuses) {
                results = results.filter((d) => allowedStatuses.includes(d.status));
            }
            setDeadlines(results);
        } catch (error) {
            console.error('Error fetching AI Calendar deadlines:', error);
            showToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load AI Calendar deadlines',
                life: 3000
            });
        } finally {
            setLoadingDeadlines(false);
        }
    }, [deadlineStatusFilter, getBackendStatusesForFilter, matterFilter, showToast]);

    const refreshAll = useCallback(

    async () => {
        await Promise.all([fetchStats(), fetchEmails(), fetchDeadlines()]);
        setLastRefresh(new Date());
    }, [fetchStats, fetchEmails, fetchDeadlines]);

    const isProcessingEmail = useCallback(

    (email) => (
        email.status === 'RECEIVED' ||
        email.status === 'PARSED' ||
        email.status === 'EXTRACTED'
    ), []);

    const isPendingDeadline = useCallback(

    (deadline) => (
        deadline.status === 'EXTRACTED' ||
        deadline.status === 'PENDING_APPROVAL'
    ), []);

    const computeShouldAutoRefresh = useCallback(

    () => {
        const hasProcessingEmails = emails.some(isProcessingEmail);
        const hasPendingDeadlines = deadlines.some(isPendingDeadline);
        return hasProcessingEmails || hasPendingDeadlines;
    }, [deadlines, emails, isPendingDeadline, isProcessingEmail]);

    const shouldAutoRefresh = useMemo(computeShouldAutoRefresh, [computeShouldAutoRefresh]);

    const runAutoRefreshCycle = useCallback(

    () => {
        refreshAll();
    }, [refreshAll]);

    const setupAutoRefresh = useCallback(

    () => {
        if (!shouldAutoRefresh) return undefined;
        const intervalId = setInterval(runAutoRefreshCycle, 30000);
        return () => clearInterval(intervalId);
    }, [runAutoRefreshCycle, shouldAutoRefresh]);

    const handleManualRefresh = useCallback(

    () => {
        refreshAll();
    }, [refreshAll]);

    const handleEmailStatusChange = useCallback(

    (event) => {
        setEmailStatusFilter(event.value);
    }, []);

    const handleDeadlineStatusChange = useCallback(

    (event) => {
        setDeadlineStatusFilter(event.value);
    }, []);

    const handleMatterFilterChange = useCallback(

    (event) => {
        setMatterFilter(event.target.value);
    }, []);

    const formatTimestamp = useCallback(

    (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleString();
    }, []);

    const getDeadlineDisplayStatus = useCallback(

    (status) => {
        if (status === 'EXTRACTED' || status === 'PENDING_APPROVAL') return 'Pending';
        if (status === 'APPROVED' || status === 'SYNCED') return 'Approved';
        if (status === 'REJECTED') return 'Rejected';
        if (status === 'MODIFIED') return 'Modified';
        return status;
    }, []);

    const getStatusSeverity = useCallback(

    (status) => {
        if (status === 'ERROR' || status === 'REJECTED') return 'danger';
        if (status === 'SYNCED' || status === 'APPROVED') return 'success';
        if (status === 'MODIFIED') return 'info';
        if (status === 'PENDING_APPROVAL' || status === 'EXTRACTED') return 'warning';
        if (status === 'PARSED' || status === 'RECEIVED') return 'info';
        return null;
    }, []);

    const renderEmailStatus = useCallback(

    (rowData) => (
        <AppTag value={rowData.status} severity={getStatusSeverity(rowData.status)} />
    ), [getStatusSeverity]);

    const renderDeadlineStatus = useCallback(

    (rowData) => (
        <AppTag value={getDeadlineDisplayStatus(rowData.status)} severity={getStatusSeverity(rowData.status)} />
    ), [getDeadlineDisplayStatus, getStatusSeverity]);

    const getConfidenceInfo = useCallback(

    (score) => {
        const pct = score != null ? Math.round(score * 100) : 0;
        if (score >= 0.90) return { label: `${pct}% High`, severity: 'success' };
        if (score >= 0.70) return { label: `${pct}% Medium`, severity: 'warning' };
        return { label: `${pct}% Low`, severity: 'danger' };
    }, []);

    const renderConfidenceBadge = useCallback(

    (rowData) => {
        if (rowData.confidence_score == null) return '-';
        const info = getConfidenceInfo(rowData.confidence_score);
        return <AppTag value={info.label} severity={info.severity} rounded />;
    }, [getConfidenceInfo]);

    const CLASSIFICATION_LABELS = {
        'OUR_DEADLINE': 'Our Deadline',
        'OUR_APPEARANCE_REQUIRED': 'Our Appearance',
        'COURT_DEADLINE': 'Court Deadline',
        'OPPONENT_DEADLINE': 'Opponent Deadline'
    };

    const renderClassification = useCallback(

    (rowData) => {
        const label = CLASSIFICATION_LABELS[rowData.deadline_classification] || rowData.deadline_classification || '-';
        return label;
    }, []);

    const renderDescription = useCallback(

    (rowData) => {
        if (!rowData.description) return '-';
        const truncated = rowData.description.length > 80
            ? rowData.description.slice(0, 80) + '...'
            : rowData.description;
        if (rowData.description.length > 80) {
            return (
                <AppTooltip content={rowData.description} position="top">
                    <span>{truncated}</span>
                </AppTooltip>
            );
        }
        return truncated;
    }, []);

    const renderDeadlineTime = useCallback(

    (rowData) => {
        if (!rowData.deadline_time) return rowData.all_day ? 'All Day' : '-';
        return rowData.deadline_time.slice(0, 5);
    }, []);

    const LOCATION_TYPE_LABELS = {
        'PHYSICAL': 'In-Person',
        'ZOOM': 'Zoom',
        'PHONE': 'Phone',
        'NONE': ''
    };

    const renderLocation = useCallback(

    (rowData) => {
        if (!rowData.location) return '-';
        const typeLabel = LOCATION_TYPE_LABELS[rowData.location_type];
        const prefix = typeLabel ? `${typeLabel}: ` : '';
        const full = prefix + rowData.location;
        const truncated = full.length > 40 ? full.slice(0, 40) + '...' : full;
        if (full.length > 40) {
            return (
                <AppTooltip content={full} position="top">
                    <span>{truncated}</span>
                </AppTooltip>
            );
        }
        return truncated;
    }, []);

    const renderAiReasoning = useCallback(

    (rowData) => {
        if (!rowData.ai_reasoning) return '-';
        const truncated = rowData.ai_reasoning.length > 60
            ? rowData.ai_reasoning.slice(0, 60) + '...'
            : rowData.ai_reasoning;
        return (
            <AppTooltip content={rowData.ai_reasoning} position="top">
                <span style={{ cursor: 'help' }}>{truncated}</span>
            </AppTooltip>
        );
    }, []);

    const renderEmailReceivedDate = useCallback(

    (rowData) => (
        formatTimestamp(rowData.received_date)
    ), [formatTimestamp]);

    const renderEmailProcessedDate = useCallback(

    (rowData) => (
        formatTimestamp(rowData.processed_at)
    ), [formatTimestamp]);

    const renderAttachmentExtractedAt = useCallback(

    (rowData) => (
        formatTimestamp(rowData.extracted_at)
    ), [formatTimestamp]);

    const handleEmailRowClick = useCallback(

    async (event) => {
        const emailId = event.data?.id;
        if (!emailId) return;
        try {
            const response = await api.get(`/ai-calendar/emails/${emailId}`);
            setSelectedEmailDetail(response.data);
            setDetailDialogVisible(true);
        } catch (error) {
            console.error('Error fetching AI Calendar email detail:', error);
            showToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load email details',
                life: 3000
            });
        }
    }, [showToast]);

    const handleDialogHide = useCallback(

    () => {
        setDetailDialogVisible(false);
        setSelectedEmailDetail(null);
    }, []);

    const handleApproveDeadline = useCallback(

    async (event) => {
        const deadlineId = event.currentTarget.dataset.deadlineId;
        if (!deadlineId) return;
        try {
            await api.post(`/ai-calendar/deadlines/${deadlineId}/approve`, {
                approved_by: 'Automations UI'
            });
            showToast({
                severity: 'success',
                summary: 'Approved',
                detail: 'Deadline approved and synced to calendar',
                life: 3000
            });
            refreshAll();
        } catch (error) {
            console.error('Error approving deadline:', error);
            showToast({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.error || 'Failed to approve deadline',
                life: 5000
            });
        }
    }, [refreshAll, showToast]);

    const handleRejectDeadline = useCallback(

    async (event) => {
        const deadlineId = event.currentTarget.dataset.deadlineId;
        if (!deadlineId) return;
        const reason = window.prompt('Reason for rejection (optional):', '');
        if (reason === null) return;
        try {
            await api.post(`/ai-calendar/deadlines/${deadlineId}/reject`, {
                rejected_by: 'Automations UI',
                reason
            });
            showToast({
                severity: 'info',
                summary: 'Rejected',
                detail: 'Deadline rejected',
                life: 3000
            });
            refreshAll();
        } catch (error) {
            console.error('Error rejecting deadline:', error);
            showToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to reject deadline',
                life: 3000
            });
        }
    }, [refreshAll, showToast]);

    const handleOpenModify = useCallback(

    async (event) => {
        const deadlineId = event.currentTarget.dataset.deadlineId;
        if (!deadlineId) return;
        try {
            const response = await api.get(`/ai-calendar/deadlines/${deadlineId}`);
            setModifyingDeadline(response.data);
            setModifyDate(response.data.deadline_date || '');
            setModifyReason('');
            setModifyDialogVisible(true);
        } catch (error) {
            console.error('Error fetching deadline for modify:', error);
            showToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load deadline details',
                life: 3000
            });
        }
    }, [showToast]);

    const handleModifySubmit = useCallback(

    async () => {
        if (!modifyingDeadline) return;
        setModifySubmitting(true);
        try {
            const payload = {};
            if (modifyDate && modifyDate !== modifyingDeadline.deadline_date) {
                payload.deadline_date = modifyDate;
            }
            if (modifyReason) {
                payload.modification_reason = modifyReason;
            }
            if (Object.keys(payload).length === 0) {
                showToast({
                    severity: 'warn',
                    summary: 'No Changes',
                    detail: 'No modifications were made',
                    life: 3000
                });
                return;
            }
            payload.status = 'MODIFIED';
            await api.put(`/ai-calendar/deadlines/${modifyingDeadline.id}`, payload);
            showToast({
                severity: 'success',
                summary: 'Modified',
                detail: 'Deadline modified and approved',
                life: 3000
            });
            setModifyDialogVisible(false);
            setModifyingDeadline(null);
            refreshAll();
        } catch (error) {
            console.error('Error modifying deadline:', error);
            showToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to modify deadline',
                life: 3000
            });
        } finally {
            setModifySubmitting(false);
        }
    }, [modifyingDeadline, modifyDate, modifyReason, refreshAll, showToast]);

    const handleModifyDialogHide = useCallback(

    () => {
        setModifyDialogVisible(false);
        setModifyingDeadline(null);
    }, []);

    const renderDeadlineActions = useCallback(

    (rowData) => {
        const isFinal = rowData.status === 'SYNCED' || rowData.status === 'REJECTED' || rowData.status === 'APPROVED' || rowData.status === 'MODIFIED';
        return (
            <div className="flex gap-2">
                <AppButton
                    icon="pi pi-check"
                    variant="secondary"
                    disabled={isFinal}
                    data-deadline-id={rowData.id}
                    onClick={handleApproveDeadline}
                    title="Approve"
                />
                <AppButton
                    icon="pi pi-times"
                    variant="secondary"
                    disabled={isFinal}
                    data-deadline-id={rowData.id}
                    onClick={handleRejectDeadline}
                    title="Reject"
                />
                <AppButton
                    icon="pi pi-pencil"
                    variant="secondary"
                    disabled={isFinal}
                    data-deadline-id={rowData.id}
                    onClick={handleOpenModify}
                    title="Modify"
                />
            </div>
        );
    }, [handleApproveDeadline, handleRejectDeadline, handleOpenModify]);

    const renderModifyDialog = useCallback(

    () => {
        if (!modifyingDeadline) return null;
        return (
            <AppModal
                title="Modify Deadline"
                visible={modifyDialogVisible}
                width="500px"
                onClose={handleModifyDialogHide}
            >
                <div className="grid">
                    <div className="col-12">
                        <div className="mb-3">
                            <strong>Type:</strong> {modifyingDeadline.deadline_type || '-'}
                        </div>
                        <div className="mb-3">
                            <strong>Current Date:</strong> {modifyingDeadline.deadline_date || '-'}
                        </div>
                        {modifyingDeadline.description && (
                            <div className="mb-3">
                                <strong>Description:</strong> {modifyingDeadline.description}
                            </div>
                        )}
                        {modifyingDeadline.ai_reasoning && (
                            <div className="mb-3">
                                <strong>AI Reasoning:</strong> {modifyingDeadline.ai_reasoning}
                            </div>
                        )}
                    </div>
                    <div className="col-12">
                        <label htmlFor="modify-date" className="block mb-2 font-semibold">New Date</label>
                        <input
                            id="modify-date"
                            type="date"
                            value={modifyDate}
                            onChange={(e) => setModifyDate(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: '6px',
                                border: '1px solid var(--surface-border)',
                                backgroundColor: 'var(--surface-section)',
                                color: 'var(--text-color)',
                                fontSize: '0.875rem'
                            }}
                        />
                    </div>
                    <div className="col-12 mt-2">
                        <label htmlFor="modify-reason" className="block mb-2 font-semibold">Reason for Change</label>
                        <AppTextarea
                            id="modify-reason"
                            value={modifyReason}
                            onChange={(e) => setModifyReason(e.target.value)}
                            placeholder="Why is this date being changed?"
                            rows={3}
                        />
                    </div>
                    <div className="col-12 mt-3 flex justify-content-end gap-2">
                        <AppButton
                            label="Cancel"
                            variant="secondary"
                            onClick={handleModifyDialogHide}
                        />
                        <AppButton
                            label={modifySubmitting ? 'Saving...' : 'Save & Approve'}
                            disabled={modifySubmitting}
                            onClick={handleModifySubmit}
                        />
                    </div>
                </div>
            </AppModal>
        );
    }, [modifyDialogVisible, modifyingDeadline, modifyDate, modifyReason, modifySubmitting, handleModifyDialogHide, handleModifySubmit]);

    const renderEmailDetailDialog = useCallback(

    () => {
        if (!selectedEmailDetail) return null;
        return (
            <AppModal
                title="AI Calendar Email Details"
                visible={detailDialogVisible}
                width="900px"
                onClose={handleDialogHide}
            >
                <div className="grid">
                    <div className="col-12">
                        <div className="surface-card p-3 border-round shadow-1 mb-3">
                            <h3 className="mt-0 mb-3" style={{ color: 'var(--text-color)' }}>Email</h3>
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <strong>Subject:</strong> {selectedEmailDetail.subject || '-'}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>From:</strong> {selectedEmailDetail.from_address || '-'}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>Received:</strong> {formatTimestamp(selectedEmailDetail.received_date)}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>Status:</strong> {selectedEmailDetail.status}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12">
                        <div className="surface-card p-3 border-round shadow-1 mb-3">
                            <h3 className="mt-0 mb-3" style={{ color: 'var(--text-color)' }}>Attachments</h3>
                            {selectedEmailDetail.attachments && selectedEmailDetail.attachments.length > 0 ? (
                                <AppDataTable value={selectedEmailDetail.attachments} responsiveLayout="scroll">
                                    <AppColumn field="filename" header="File" />
                                    <AppColumn field="mime_type" header="Type" />
                                    <AppColumn field="size_bytes" header="Size (bytes)" />
                                    <AppColumn field="extraction_method" header="Extraction" />
                                    <AppColumn field="extracted_at" header="Extracted At" body={renderAttachmentExtractedAt} />
                                </AppDataTable>
                            ) : (
                                <div>No attachments recorded for this email.</div>
                            )}
                        </div>
                    </div>
                    <div className="col-12">
                        <div className="surface-card p-3 border-round shadow-1 mb-3">
                            <h3 className="mt-0 mb-3" style={{ color: 'var(--text-color)' }}>Deadlines</h3>
                            {selectedEmailDetail.deadlines && selectedEmailDetail.deadlines.length > 0 ? (
                                <AppDataTable value={selectedEmailDetail.deadlines} responsiveLayout="scroll">
                                    <AppColumn field="status" header="Status" body={renderDeadlineStatus} />
                                    <AppColumn field="deadline_type" header="Type" />
                                    <AppColumn field="deadline_date" header="Date" />
                                    <AppColumn field="deadline_time" header="Time" body={renderDeadlineTime} />
                                    <AppColumn field="description" header="Description" body={renderDescription} style={{ maxWidth: '200px' }} />
                                    <AppColumn field="matter_number" header="Matter" />
                                    <AppColumn field="deadline_classification" header="Classification" body={renderClassification} />
                                    <AppColumn field="confidence_score" header="Confidence" body={renderConfidenceBadge} />
                                    <AppColumn field="location" header="Location" body={renderLocation} />
                                    <AppColumn field="ai_reasoning" header="AI Reasoning" body={renderAiReasoning} style={{ maxWidth: '180px' }} />
                                </AppDataTable>
                            ) : (
                                <div>No deadlines recorded for this email.</div>
                            )}
                        </div>
                    </div>
                </div>
            </AppModal>
        );
    }, [detailDialogVisible, formatTimestamp, handleDialogHide, renderAttachmentExtractedAt, renderDeadlineStatus, selectedEmailDetail]);

    useEffect(fetchStats, [fetchStats]);
    useEffect(fetchEmails, [fetchEmails]);
    useEffect(fetchDeadlines, [fetchDeadlines]);
    useEffect(setupAutoRefresh, [setupAutoRefresh]);

    return (
        <div className="ai-calendar-pipeline">
            <div className="surface-card p-4 mb-4 border-round">
                <div className="flex align-items-center justify-content-between">
                    <div className="text-xl font-medium">AI Calendar Overview</div>
                    <AppButton
                        label="Refresh"
                        icon="pi pi-refresh"
                        onClick={handleManualRefresh}
                    />
                </div>
                <div className="text-sm text-600 mt-2">
                    Last refreshed: {lastRefresh.toLocaleTimeString()}
                    {shouldAutoRefresh && ' • Auto-refreshing every 30 seconds'}
                </div>
                <hr style={{ border: 'none', borderTop: '1px solid var(--surface-border)', margin: '1rem 0' }} />
                {loadingStats ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '120px' }}>
                        <AppSpinner />
                    </div>
                ) : (
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="surface-card p-3 border-round shadow-1">
                                <h3 className="mt-0 mb-3" style={{ color: 'var(--text-color)' }}>Emails</h3>
                                <div className="grid">
                                    <div className="col-6">
                                        <strong>Total:</strong> {stats?.emails?.total ?? 0}
                                    </div>
                                    <div className="col-6">
                                        <strong>Processing:</strong> {(stats?.emails?.by_status?.RECEIVED ?? 0) + (stats?.emails?.by_status?.PARSED ?? 0) + (stats?.emails?.by_status?.EXTRACTED ?? 0)}
                                    </div>
                                    <div className="col-6">
                                        <strong>Complete:</strong> {stats?.emails?.by_status?.PROCESSED ?? 0}
                                    </div>
                                    <div className="col-6">
                                        <strong>Error:</strong> {stats?.emails?.by_status?.ERROR ?? 0}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="surface-card p-3 border-round shadow-1">
                                <h3 className="mt-0 mb-3" style={{ color: 'var(--text-color)' }}>Deadlines</h3>
                                <div className="grid">
                                    <div className="col-6">
                                        <strong>Total:</strong> {stats?.deadlines?.total ?? 0}
                                    </div>
                                    <div className="col-6">
                                        <strong>Pending:</strong> {(stats?.deadlines?.by_status?.EXTRACTED ?? 0) + (stats?.deadlines?.by_status?.PENDING_APPROVAL ?? 0)}
                                    </div>
                                    <div className="col-6">
                                        <strong>Approved:</strong> {(stats?.deadlines?.by_status?.SYNCED ?? 0) + (stats?.deadlines?.by_status?.APPROVED ?? 0)}
                                    </div>
                                    <div className="col-6">
                                        <strong>Rejected:</strong> {stats?.deadlines?.by_status?.REJECTED ?? 0}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="surface-card p-4 mb-4 border-round">
                <div className="flex align-items-center justify-content-between mb-3">
                    <div className="text-lg font-medium">Emails</div>
                </div>
                <div className="grid mb-3">
                    <div className="col-12 md:col-4">
                        <label htmlFor="ai-email-status" className="block mb-2">Status</label>
                        <AppDropdown
                            id="ai-email-status"
                            value={emailStatusFilter}
                            options={emailStatusOptions}
                            onChange={handleEmailStatusChange}
                        />
                    </div>
                </div>
                {loadingEmails && emails.length === 0 ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                        <AppSpinner />
                    </div>
                ) : (
                    <AppDataTable
                        value={emails}
                        paginator
                        rows={25}
                        rowsPerPageOptions={[10, 25, 50]}
                        emptyMessage="No AI Calendar emails found"
                        responsiveLayout="scroll"
                        stripedRows
                        onRowClick={handleEmailRowClick}
                        selectionMode="single"
                    >
                        <AppColumn field="status" header="Status" body={renderEmailStatus} sortable />
                        <AppColumn field="subject" header="Subject" />
                        <AppColumn field="from_address" header="From" />
                        <AppColumn field="received_date" header="Received" body={renderEmailReceivedDate} sortable />
                        <AppColumn field="processed_at" header="Processed" body={renderEmailProcessedDate} />
                    </AppDataTable>
                )}
            </div>

            <div className="surface-card p-4 border-round">
                <div className="flex align-items-center justify-content-between mb-3">
                    <div className="text-lg font-medium">Deadlines</div>
                </div>
                <div className="grid mb-3">
                    <div className="col-12 md:col-4">
                        <label htmlFor="ai-deadline-status" className="block mb-2">Status</label>
                        <AppDropdown
                            id="ai-deadline-status"
                            value={deadlineStatusFilter}
                            options={deadlineStatusOptions}
                            onChange={handleDeadlineStatusChange}
                        />
                    </div>
                    <div className="col-12 md:col-4">
                        <label htmlFor="ai-deadline-matter" className="block mb-2">Matter Number</label>
                        <AppTextInput
                            id="ai-deadline-matter"
                            value={matterFilter}
                            onChange={handleMatterFilterChange}
                            placeholder="Search by matter number"
                        />
                    </div>
                </div>
                {loadingDeadlines && deadlines.length === 0 ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                        <AppSpinner />
                    </div>
                ) : (
                    <AppDataTable
                        value={deadlines}
                        paginator
                        rows={25}
                        rowsPerPageOptions={[10, 25, 50]}
                        emptyMessage="No AI Calendar deadlines found"
                        responsiveLayout="scroll"
                        stripedRows
                        sortField="deadline_date"
                        sortOrder={-1}
                    >
                        <AppColumn field="status" header="Status" body={renderDeadlineStatus} sortable />
                        <AppColumn field="deadline_type" header="Type" />
                        <AppColumn field="deadline_date" header="Date" sortable />
                        <AppColumn field="deadline_time" header="Time" body={renderDeadlineTime} />
                        <AppColumn field="description" header="Description" body={renderDescription} style={{ maxWidth: '200px' }} />
                        <AppColumn field="matter_number" header="Matter" />
                        <AppColumn field="deadline_classification" header="Classification" body={renderClassification} sortable />
                        <AppColumn field="confidence_score" header="Confidence" body={renderConfidenceBadge} sortable />
                        <AppColumn field="location" header="Location" body={renderLocation} />
                        <AppColumn field="ai_reasoning" header="AI Reasoning" body={renderAiReasoning} style={{ maxWidth: '180px' }} />
                        <AppColumn header="Actions" body={renderDeadlineActions} style={{ width: '180px' }} />
                    </AppDataTable>
                )}
            </div>

            {renderEmailDetailDialog()}
            {renderModifyDialog()}
        </div>
    );
}

export default AICalendarPipeline;
